type IncidentCuePriority = 'Critical' | 'Standard' | 'Info'

type AudioContextConstructor = typeof AudioContext

function resolveAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null
  const win = window as typeof window & {
    AudioContext?: AudioContextConstructor
    webkitAudioContext?: AudioContextConstructor
  }
  return win.AudioContext ?? win.webkitAudioContext ?? null
}

let sharedContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  const Ctor = resolveAudioContextConstructor()
  if (!Ctor) return null
  if (!sharedContext) sharedContext = new Ctor()
  return sharedContext
}

function playBeep(
  context: AudioContext,
  frequency: number,
  startAt: number,
  durationSeconds: number,
  peakGain: number,
): void {
  const oscillator = context.createOscillator()
  const gain = context.createGain()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(frequency, startAt)
  gain.gain.setValueAtTime(0, startAt)
  gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSeconds)
  oscillator.connect(gain)
  gain.connect(context.destination)
  oscillator.start(startAt)
  oscillator.stop(startAt + durationSeconds)
}

export function playIncidentChime(priority: IncidentCuePriority): void {
  try {
    const context = getAudioContext()
    if (!context) return
    if (context.state === 'suspended') void context.resume()

    const now = context.currentTime
    if (priority === 'Critical') {
      playBeep(context, 988, now, 0.16, 0.18)
      playBeep(context, 1319, now + 0.18, 0.2, 0.18)
    } else {
      playBeep(context, 660, now, 0.16, 0.1)
    }
  } catch {
    // Audio is a non-essential cue; never let it break the app.
  }
}

type DesktopNotificationInput = {
  incidentId: number
  priority: IncidentCuePriority
  title: string
  body: string | null
}

export function isDesktopNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

export function getDesktopNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isDesktopNotificationSupported()) return 'unsupported'
  return Notification.permission
}

export function requestDesktopNotificationPermission(): void {
  try {
    if (!isDesktopNotificationSupported()) return
    if (Notification.permission !== 'default') return
    void Notification.requestPermission()
  } catch {
    // Permission request can throw in some browsers; ignore.
  }
}

export function showIncidentDesktopNotification({
  incidentId,
  priority,
  title,
  body,
}: DesktopNotificationInput): void {
  try {
    if (!isDesktopNotificationSupported()) return
    if (Notification.permission !== 'granted') return

    const severityLabel = priority === 'Critical' ? 'Critical' : priority === 'Info' ? 'Info' : 'New'
    const notification = new Notification(`${severityLabel} incident: ${title}`, {
      body: body ?? undefined,
      tag: `incident-${incidentId}`,
      requireInteraction: priority === 'Critical',
    })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  } catch {
    // Notifications are a non-essential cue; never let them break the app.
  }
}
